import { useState } from "react";

import { useAnalytics, useEnterprisePricingCalculator } from "../../components";
import { UiPricingCalculatorModel } from "../../models";
import { EnterprisePriceCalculationsError } from "../../utils/enterprise-pricing-calculator";

import {
    CalculatorHeading,
    InfoLabel,
    NumberInput,
    Price,
    PriceContainer,
    PriceGrid,
    PriceItem,
    PriceLabel
} from "./UiPricingCalculator.styles";

export type UiPricingCalculatorProps = {
    readonly model: UiPricingCalculatorModel;
};

const UiPricingCalculator = ({ model }: UiPricingCalculatorProps) => {
    const {
        title,
        userCountLabel,
        priceLabel,
        annualTotalText,
        perUserPerMonthText,
        usdText,
        generalError,
        minUsersError,
        maxUsersError,
        plan
    } = model.content;
    const minUsers = 50;
    const maxUsers = 10000000;
    const calculator = useEnterprisePricingCalculator(
        plan.content.pricing,
        minUsers,
        maxUsers
    );
    const [userCount, setUserCount] = useState<number | "">(minUsers);
    const [[error, prices], setPrice] = useState(
        calculator.getPricesByUserCount(minUsers)
    );
    const [used, setUsed] = useState(false);
    const { sendTrackEvent } = useAnalytics();
    const hasError = !!error;
    const handleOnChange = (value: string) => {
        const numberString = value.replace(/\D/g, "");
        const numberValue =
            numberString.length > 1 && numberString.startsWith("0")
                ? parseInt(numberString.replace(/^0+/, ""))
                : parseInt(numberString);
        setUserCount(isNaN(numberValue) ? "" : numberValue);
        setPrice(calculator.getPricesByUserCount(numberValue));

        //only send a single event when the user interacts with the input
        if (!used) {
            sendTrackEvent("usedPricingCalculator", {
                event: "used",
                eventComponent: model.type,
                eventContainer: "content",
                label: title,
                labelEnglish: "Pricing Calculator",
                uuid: model.id
            });
            setUsed(true);
        }
    };

    const inputId = `uipc-${model.id}`;

    return (
        <div data-testid="ui-pricing-calculator">
            <CalculatorHeading>{title}</CalculatorHeading>
            <div>
                <div>
                    <label htmlFor={inputId}>{userCountLabel}</label>
                </div>
                <div>
                    <NumberInput
                        data-testid="ui-pricing-calculator-input"
                        id={inputId}
                        max={maxUsers}
                        min={minUsers}
                        onChange={(event) => handleOnChange(event.target.value)}
                        step="1"
                        type="number"
                        value={userCount}
                    />
                </div>
            </div>
            {hasError && (
                <InfoLabel data-testid="ui-pricing-calculator-error">
                    {error === EnterprisePriceCalculationsError.MIN_USERS_ERROR
                        ? minUsersError
                        : error ===
                          EnterprisePriceCalculationsError.MAX_USERS_ERROR
                        ? maxUsersError
                        : generalError}
                </InfoLabel>
            )}
            {!hasError && (
                <PriceContainer>
                    <PriceLabel>{priceLabel}</PriceLabel>
                    <PriceGrid>
                        <PriceItem>
                            <Price data-testid="ui-pricing-calculator-monthly-price">
                                <small>$</small>
                                <span>{prices.monthlyPricePerUser}</span>
                                <small>{usdText}</small>
                            </Price>
                            <InfoLabel>{perUserPerMonthText}</InfoLabel>
                        </PriceItem>
                        <PriceItem>
                            <Price data-testid="ui-pricing-calculator-annual-price">
                                <small>$</small>
                                <span>{prices.annualPrice}</span>
                                <small>{usdText}</small>
                            </Price>
                            <InfoLabel>{annualTotalText}</InfoLabel>
                        </PriceItem>
                    </PriceGrid>
                </PriceContainer>
            )}
        </div>
    );
};

export default UiPricingCalculator;
